import { forwardRef } from 'react';

import {
  Button,
  type ButtonCssVariables,
  type ButtonProps,
  type ElementProps,
} from '@mantine/core';
import {
  type ButtonVariant,
  type ColorIntent,
  buttonColor,
  buttonFilledBackgroundState,
  resolveButtonColors,
  resolveSemanticColor,
} from '@teo/design-tokens';

/**
 * The canonical button. Every button in the app is an `AppButton`; raw Mantine
 * `<Button>` is banned outside the design system.
 *
 * Two required, closed-union axes — `variant` (visual style) × `intent` (semantic
 * role) — resolve every color from `@teo/design-tokens`, so there is no per-hue
 * special-casing here and no hardcoded hex. Mantine's `<Button>` supplies all
 * behavior (sizes, sections, loading, disabled, focus ring, ref); we override only
 * its colors, through its documented CSS variables via the `vars` prop so the
 * caller's `style`/`styles` pass through untouched. `vars` itself is closed off —
 * it is the channel the token colors flow through, and a caller-supplied resolver
 * would silently replace them wholesale.
 */
export interface AppButtonProps
  extends
    Omit<ButtonProps, 'variant' | 'color' | 'gradient' | 'autoContrast' | 'vars'>,
    ElementProps<'button', keyof ButtonProps> {
  /** Visual style. */
  variant: ButtonVariant;
  /** Semantic role — drives the color, never the caller's raw hex. */
  intent: ColorIntent;
}

/** Our variant names line up 1:1 with Mantine's, but stay explicit for clarity. */
const MANTINE_VARIANT: Record<ButtonVariant, string> = {
  filled: 'filled',
  outline: 'outline',
  light: 'light',
  subtle: 'subtle',
};

/**
 * The Mantine Button CSS variables for a variant + intent. Hover keeps the label
 * color fixed (ink never changes on hover): a filled button darkens its fill through
 * the interaction ramp; outline and subtle pick up the light tint of the same intent;
 * light steps to its border tint — its resting fill already IS the light tint, so
 * reusing it would leave light buttons with no hover feedback at all, and the tint
 * border sits one-plus steps darker on every intent's ramp. Disabled is left to
 * Mantine's own selector, which `buttonDisabled` in the token layer documents.
 */
function computeButtonColorVars(
  variant: ButtonVariant,
  intent: ColorIntent,
): Partial<Record<ButtonCssVariables['root'], string>> {
  const { bg, fg, border } = resolveButtonColors(variant, intent);
  // Exhaustive over ButtonVariant so a future variant fails to compile here — not
  // just at MANTINE_VARIANT — forcing a deliberate hover choice rather than silently
  // inheriting the outline/subtle formula.
  let hoverBg: string;
  switch (variant) {
    case 'filled':
      hoverBg = resolveSemanticColor(buttonFilledBackgroundState[intent].hover);
      break;
    case 'light':
      hoverBg = resolveSemanticColor(buttonColor.light[intent].border);
      break;
    case 'outline':
    case 'subtle':
      hoverBg = resolveSemanticColor(buttonColor.light[intent].bg);
      break;
    default: {
      const _exhaustive: never = variant;
      throw new Error(`Unhandled button variant: ${_exhaustive}`);
    }
  }

  return {
    '--button-bg': resolveSemanticColor(bg),
    '--button-color': resolveSemanticColor(fg),
    '--button-bd': `1.5px solid ${resolveSemanticColor(border)}`,
    '--button-hover': hoverBg,
    '--button-hover-color': resolveSemanticColor(fg),
  };
}

export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(function AppButton(
  { variant, intent, ...props },
  ref,
) {
  return (
    // props spreads first so the token-derived variant/vars always win — the typed
    // Omit closes the escape hatch at compile time, this closes it at runtime.
    <Button
      {...props}
      ref={ref}
      variant={MANTINE_VARIANT[variant]}
      vars={() => ({ root: computeButtonColorVars(variant, intent) })}
    />
  );
});

export const __private = { computeButtonColorVars, MANTINE_VARIANT };
